import React, { useState, useEffect } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import styled from 'styled-components';
import { collection, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';

const AdminContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #1e4d92 0%, #2d8fe5 100%);
  padding: 2rem;
`;

const AdminCard = styled.div`
  max-width: 1200px;
  margin: 2rem auto;
  padding: 2rem;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.1);
`;

const Title = styled.h1`
  font-size: 2.5rem;
  color: #1e4d92;
  text-align: center;
  margin-bottom: 2rem;
`;

const QRCodeContainer = styled.div`
  margin: 1rem 0;
  padding: 1rem;
  background: white;
  border-radius: 8px;
  display: inline-block;
`;

const UserCard = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  padding: 1.5rem;
  margin-bottom: 1rem;
  display: grid;
  grid-template-columns: 100px 1fr auto;
  gap: 1.5rem;
  background: white;
`;

const UserPhoto = styled.img`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  object-fit: cover;
`;

const UserInfo = styled.div`
  h3 {
    color: #1e4d92;
    margin-bottom: 1rem;
  }

  p {
    margin: 0.5rem 0;
    color: #333;
  }
`;

const SearchBar = styled.input`
  width: 100%;
  padding: 1rem;
  margin-bottom: 2rem;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1.1rem;

  &:focus {
    border-color: #1e4d92;
    outline: none;
  }
`;

const DeleteButton = styled.button`
  background: #dc3545;
  color: white;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-bottom: 1rem;

  &:hover {
    background: #c82333;
  }
`;

const DeleteUserButton = styled.button`
  background: #dc3545;
  color: white;
  padding: 0.5rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-left: 1rem;

  &:hover {
    background: #c82333;
  }
`;

const AdminPage = () => {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchUsers = async () => {
      const querySnapshot = await getDocs(collection(db, 'users'));
      const usersData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setUsers(usersData);
    };

    fetchUsers();
  }, []);

  const clearDuplicates = async () => {
    try {
      const seen = new Map();
      const toDelete = [];
      
      const querySnapshot = await getDocs(collection(db, 'users'));
      
      querySnapshot.forEach((docSnapshot) => {
        const data = docSnapshot.data();
        const key = `${data.name}-${data.email}`; // chave única baseada em nome e email
        
        if (seen.has(key)) {
          toDelete.push(docSnapshot.id);
        } else {
          seen.set(key, true);
        }
      });

      // Deletar duplicatas
      for (const docId of toDelete) {
        await deleteDoc(doc(db, 'users', docId));
      }

      // Recarregar a lista
      const newSnapshot = await getDocs(collection(db, 'users'));
      const usersData = newSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setUsers(usersData);

      alert(`${toDelete.length} registros duplicados foram removidos.`);
    } catch (error) {
      console.error('Erro ao limpar duplicatas:', error);
      alert('Erro ao limpar registros duplicados.');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Tem certeza que deseja excluir este registro?')) {
      try {
        await deleteDoc(doc(db, 'users', userId));
        setUsers(users.filter(user => user.id !== userId));
      } catch (error) {
        console.error('Erro ao deletar usuário:', error);
        alert('Erro ao deletar usuário');
      }
    }
  };

  return (
    <AdminContainer>
      <AdminCard>
        <Title>Painel Administrativo</Title>
        <DeleteButton onClick={clearDuplicates}>
          Limpar Registros Duplicados
        </DeleteButton>
        <SearchBar
          type="text"
          placeholder="Pesquisar por nome ou email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {users.filter(user =>
          user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.email.toLowerCase().includes(searchTerm.toLowerCase())
        ).map(user => (
          <UserCard key={user.id}>
            <UserPhoto src={user.photo} alt={user.name} />
            <UserInfo>
              <h3>{user.name}</h3>
              <p><strong>Email:</strong> {user.email}</p>
              <p><strong>Telefone:</strong> {user.phone}</p>
              <p><strong>Tipo Sanguíneo:</strong> {user.bloodType}</p>
              <p><strong>Contato de Emergência:</strong> {user.emergencyContact}</p>
              <p><strong>Telefone de Emergência:</strong> {user.emergencyPhone}</p>
              <p><strong>Condições Médicas:</strong> {user.medicalConditions}</p>
              <DeleteUserButton onClick={() => handleDeleteUser(user.id)}>
                Excluir
              </DeleteUserButton>
            </UserInfo>
            <QRCodeContainer>
              <QRCodeCanvas 
                value={`${window.location.origin}/profile/${user.id}`}
                size={128}
                level="H"
              />
            </QRCodeContainer>
          </UserCard>
        ))}
      </AdminCard>
    </AdminContainer>
  );
};

export default AdminPage;